'use client';
import React, { useEffect, useRef, useState } from 'react';

export default function BookAppointment() {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', phone: '', email: '', service: '', date: '', message: '' });
  };

  const services = [
    'Veneers',
    'Smile Design',
    'Full Mouth Rehabilitation',
    'Full Mouth Implant Restoration',
    'General Consultation',
    'Other',
  ];

  return (
    <section id="contact" ref={ref} className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Info */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 bg-secondary rounded-full px-4 py-1.5 mb-6">
              <span className="font-inter text-xs font-semibold text-dark/60 uppercase tracking-widest">Book Appointment</span>
            </div>
            <h2 className="font-unbounded font-black text-dark text-3xl lg:text-4xl xl:text-5xl leading-[1.08] mb-6">
              Book Your
              <br />
              Appointment
              <br />
              <span className="relative inline-block">
                in Minutes
                <span className="absolute -bottom-1 left-0 w-full h-[3px] bg-primary rounded-full" />
              </span>
            </h2>
            <p className="font-inter text-dark/55 text-base leading-relaxed mb-10">
              Schedule your consultation with Dr. Abhishek Pal and take the first step toward your
              perfect smile. We'll confirm your appointment within 24 hours.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-3">
              {[
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  label: 'Phone',
                  value: '+91 XXXXX XXXXX',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  label: 'Email',
                  value: 'info@drpals.in',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  label: 'Website',
                  value: 'www.DrPals.in',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#131c15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  label: 'Working Hours',
                  value: 'Mon–Fri: 9AM–8PM | Sat: 10AM–6PM',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4 bg-secondary rounded-2xl p-4 hover:bg-primary/30 transition-all duration-200">
                  <div className="w-11 h-11 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <p className="font-inter text-xs text-dark/40 mb-0.5">{item.label}</p>
                    <p className="font-unbounded font-bold text-dark text-xs leading-snug">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-secondary rounded-3xl p-6 lg:p-8">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4 shadow-teal">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#131c15" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3 className="font-unbounded font-black text-dark text-xl mb-2">Request Sent!</h3>
                  <p className="font-inter text-dark/55 text-sm">
                    Thank you! We'll confirm your appointment within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="font-unbounded font-bold text-dark text-lg mb-6">Request Appointment</h3>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark placeholder-dark/30 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                      />
                    </div>
                    <div>
                      <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark placeholder-dark/30 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark placeholder-dark/30 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                  </div>

                  <div>
                    <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Service Required *</label>
                    <select
                      required
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Preferred Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                  </div>

                  <div>
                    <label className="font-inter text-xs font-semibold text-dark/55 mb-1.5 block">Message</label>
                    <textarea
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your dental concerns..."
                      className="w-full bg-white border border-secondary-dark rounded-xl px-4 py-3 font-inter text-sm text-dark placeholder-dark/30 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-dark text-white font-inter font-semibold text-sm py-4 rounded-xl hover:bg-dark-light transition-all duration-200 hover:shadow-card-hover flex items-center justify-center gap-2"
                  >
                    Book Appointment
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
